import { useLazyQuery, useMutation } from '@apollo/client';
import { Form, Image, Input, Modal, Switch, Tooltip } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as ArchiveIcon } from '../../../../../assets/svg/box-archive-solid.svg';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/pen-to-square-solid.svg';
import { ReactComponent as UnarchiveIcon } from '../../../../../assets/svg/right-from-bracket-solid.svg';
import {
  ARCHIVING_STATE,
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  SORT,
} from '../../../../../common/constants';
import {
  fileUpload,
  formatDate,
  hasPermission,
  openNotification,
} from '../../../../../common/utils';
import CommonPreview from '../../../../../components/CommonPreview';
import CommonTable from '../../../../../components/CommonTable';
import SearchComponent from '../../../../../components/SearchComponent';
import { CREATE_FACILITATE, UPDATE_FACILITATES } from '../graphql/Mutations';
import {
  FACILITATES_ADMIN,
  GET_FACILITATES_IMAGE_SIGNED_PUT_URL,
} from '../graphql/Queries';
import FacilitatesModal from './FacilitatesModal';

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialFilter = {
  limit: LIMIT,
  search: '',
  skip: 0,
};

const initialSort = {
  field: 'name',
  order: 'ASC',
};

const FacilitatesTable = ({
  isFacilitatesModalOpen,
  setIsFacilitatesModalOpen,
  facilitatesImages,
  setFacilitatesImages,
}) => {
  const {
    state: { permissions },
  } = useContext(AppContext);
  const [form] = Form.useForm();
  const [facilitatesList, setFacilitatesList] = useState([]);
  const [facilitatesSearchTerm, setFacilitatesSearchTerm] = useState('');
  const [isEmptyFacilitatesList, setIsEmptyFacilitatesList] = useState(false);
  const [facilitates, setFacilitates] = useState();
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isArchivePrompts, setIsArchivePrompts] = useState(false);
  const [isFacilitatesLoading, setIsFacilitatesLoading] = useState(true);
  const [isArchiveLoading, setIsArchiveLoading] = useState(false);
  const [isArchiveFilter, setIsArchiveFilter] = useState(false);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [facilitatesPreviewImage, setFacilitatesPreviewImage] = useState();

  const [createFacilitate] = useMutation(CREATE_FACILITATE, {
    onError: () => {},
  });

  const [colour, setColour] = useState('');
  const [updateFacilitate] = useMutation(UPDATE_FACILITATES, {
    onError: () => {},
  });

  const [getFacilitatesImageSignedPutUrl] = useLazyQuery(
    GET_FACILITATES_IMAGE_SIGNED_PUT_URL,
    {
      fetchPolicy: 'network-only',
      onError() {
        setIsBtnLoading(false);
      },
    },
  );

  const [facilitatesAdmin] = useLazyQuery(FACILITATES_ADMIN, {
    onCompleted: (response) => {
      setFacilitatesList(
        [...response?.facilitatesAdmin?.data]?.filter(
          (item) => item?.minAge !== 0 || item?.maxAge !== 0,
        ),
      );
      if (
        response?.facilitatesAdmin?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyFacilitatesList(true);
      } else {
        setIsEmptyFacilitatesList(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.facilitatesAdmin?.count,
      };
      setPaginationProp(pagination);
      setIsFacilitatesLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.MASTER_LIST_FACILITATE,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  useEffect(() => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsFacilitatesLoading(true);
    facilitatesAdmin({
      variables: {
        filter: {
          ...initialFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: facilitatesSearchTerm,
          isArchived: isArchiveFilter,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
  }, [isArchiveFilter]);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsFacilitatesLoading(true);
    setSortedInfo(sorter);
    facilitatesAdmin({
      variables: {
        filter: {
          ...initialFilter,
          skip,
          limit: pagination?.pageSize,
          search: facilitatesSearchTerm,
          isArchived: isArchiveFilter,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setFacilitatesSearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsFacilitatesLoading(true);
    facilitatesAdmin({
      variables: {
        filter: {
          ...initialFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          isArchived: isArchiveFilter,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
  };

  const handleArchive = async (id, isArchived) => {
    setIsArchiveLoading(true);
    const response = await updateFacilitate({
      variables: {
        where: {
          id,
        },
        data: {
          isArchived: !isArchived,
        },
      },
    });
    if (response?.data) {
      setIsArchiveLoading(false);
      setIsFacilitatesLoading(true);
      setIsArchivePrompts(false);
      setFacilitates();
      facilitatesAdmin({
        variables: {
          filter: {
            ...initialFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: facilitatesSearchTerm,
            isArchived: isArchiveFilter,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialSort,
        },
      });
    }
    setIsArchiveLoading(false);
  };

  const handleFacilitate = async (values) => {
    setIsBtnLoading(true);
    const uuid = uuidv4();
    let response;
    let image = '';

    if (values?.image?.length > 0 && !facilitatesImages?.[0]?.url) {
      const { name } = values?.image?.[0];
      const ext = name?.substring(name?.lastIndexOf('.') + 1);
      const timestamp = Date?.now();
      const filename = name?.split('.')?.slice(0, -1)?.join('.');
      const newFilename = `${timestamp}_${filename}.${ext}`;
      const fileKey = `facilitates/${uuid}/${newFilename}`;

      const res = await getFacilitatesImageSignedPutUrl({
        variables: {
          data: {
            fileName: fileKey,
            imageUuid: uuid,
          },
        },
      });
      if (res?.data) {
        try {
          try {
            await fileUpload(
              res?.data?.getFacilitatesImageSignedPutUrl?.signedUrl,
              facilitatesImages?.[0]?.originFileObj,
            );
            image = res?.data?.getFacilitatesImageSignedPutUrl?.fileName;
          } catch (error) {
            throw new Error(
              `${facilitatesImages?.name} upload failed. Please try again.`,
            );
          }
        } catch (error) {
          setIsBtnLoading(false);
          openNotification('error', error.message);
        }
      }
    }
    try {
      if (!facilitates) {
        response = await createFacilitate({
          variables: {
            data: {
              name: values?.name,
              imageUuid: uuid,
              image: facilitatesImages?.[0]?.url ? undefined : image,
              description: values?.description,
              colour,
            },
          },
        });
      } else {
        response = await updateFacilitate({
          variables: {
            where: {
              id: facilitates?.id,
            },
            data: {
              name: values?.name,
              imageUuid: facilitates?.imageUuid,
              image: facilitatesImages?.[0]?.url ? undefined : image,
              description: values?.description,
              colour,
            },
          },
        });
      }
    } catch (error) {
      setIsBtnLoading(false);
      return error;
    }
    if (response?.data) {
      setIsFacilitatesModalOpen(false);
      setIsBtnLoading(true);
      setFacilitates();
      form?.resetFields();
      facilitatesAdmin({
        variables: {
          filter: {
            ...initialFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: facilitatesSearchTerm,
            isArchived: isArchiveFilter,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialSort,
        },
      });
    }
    setIsBtnLoading(false);
  };

  const columns = [
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 250,
      align: 'left',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      className: 'max-width-column',
      render: (_, record) => record?.name?.replace(/\s/g, '\u00a0'),
    },
    {
      title: 'IMAGE',
      dataIndex: 'image',
      key: 'image',
      ellipsis: true,
      width: 100,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <span>
          {record?.image ? (
            <>
              <Image
                preview={{
                  visible: false,
                }}
                width={20}
                height={20}
                src={record?.image}
                onClick={(e) => {
                  e?.stopPropagation();
                  setVisible(true);
                  setFacilitatesPreviewImage(record?.image);
                }}
                className="pointer"
              />
            </>
          ) : (
            <span>No Image</span>
          )}
        </span>
      ),
    },

    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.description || '-',
    },
    {
      title: 'COLOR',
      dataIndex: 'colour',
      key: 'colour',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        record?.colour ? (
          <div>
            <Input
              type="color"
              allowClear
              value={record?.colour}
              placeholder="Select Color"
              className="facilitate-color-display"
              disabled
            />{' '}
            {}({record?.colour})
          </div>
        ) : (
          '-'
        ),
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      ellipsis: true,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      ellipsis: true,
      width: 150,
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
      render: (_, record) => (
        <>
          <div className="action-button">
            {isArchiveFilter ? (
              <Tooltip title="Unarchive" placement="top" zIndex={4}>
                <UnarchiveIcon
                  onClick={() => {
                    setIsArchivePrompts(true);
                    setFacilitates(record);
                  }}
                  className="mr-16 pointer svg-icon"
                />
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Edit" placement="top" zIndex={4}>
                  <EditIcon
                    onClick={() => {
                      setIsFacilitatesModalOpen(true);
                      setFacilitates(record);
                      setFacilitatesImages(
                        record?.image ? [{ url: record?.image }] : [],
                      );
                    }}
                    className="mr-16 pointer svg-icon"
                  />
                </Tooltip>
                <Tooltip title="Archive" placement="top" zIndex={4}>
                  <ArchiveIcon
                    onClick={() => {
                      setIsArchivePrompts(true);
                      setFacilitates(record);
                    }}
                    className="mr-16 pointer svg-icon"
                  />
                </Tooltip>
              </>
            )}
          </div>
        </>
      ),
    },
  ]?.filter((item) => {
    if (item?.dataIndex === 'actions' && !hasUpdatePermission) {
      return item?.hidden;
    }
    return !item?.hidden;
  });

  const locale = {
    emptyText: isEmptyFacilitatesList ? '' : <span />,
  };

  return (
    <>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isArchivePrompts}
        onOk={() => handleArchive(facilitates?.id, facilitates?.isArchived)}
        onCancel={() => {
          setIsArchivePrompts(false);
          setFacilitates();
        }}
        okButtonProps={{ loading: isArchiveLoading }}
      >
        Are you sure you want to
        <strong>
          {' '}
          {!facilitates?.isArchived
            ? ARCHIVING_STATE?.ARCHIVED
            : ARCHIVING_STATE?.UNARCHIVED}{' '}
        </strong>
        this facilitates?
      </Modal>
      <CommonPreview
        visible={visible}
        setVisible={setVisible}
        image={facilitatesPreviewImage}
        setImage={setFacilitatesPreviewImage}
      />
      <FacilitatesModal
        form={form}
        onFinish={handleFacilitate}
        loadings={isBtnLoading}
        isModalOpen={isFacilitatesModalOpen}
        setIsModalOpen={setIsFacilitatesModalOpen}
        facilitates={facilitates}
        setFacilitates={setFacilitates}
        facilitatesImages={facilitatesImages}
        setFacilitatesImages={setFacilitatesImages}
        setVisible={setVisible}
        setFacilitatesPreviewImage={setFacilitatesPreviewImage}
        colour={colour}
        setColour={setColour}
      />
      <div className="d-flex justify-end mb-16 flex-wrap">
        {hasUpdatePermission && (
          <div className="mr-16 d-flex align-center">
            <h4 className="m-0 mr-8">Only Archived</h4>{' '}
            <Switch
              size="small"
              onChange={(checked) => setIsArchiveFilter(checked)}
            />
          </div>
        )}
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>
      <CommonTable
        locale={locale}
        columns={columns}
        data={facilitatesList || []}
        loading={isFacilitatesLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
      />
    </>
  );
};

export default FacilitatesTable;
